import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { tableHeaderThStyle } from '@/constants/layout'
import { SurveyAnswersResponse, SurveyQuestion } from '@/models/survey/answers'
import { SortableTableHeader, SortKeyType } from '@/components/molecules/sortable-table-header'
import { usePickByLanguage } from '@/hooks/use-pick-by-language'

/**
 * 回答一覧ページのテーブルコンポーネント
 * アンケート回答をテーブル形式で表示
 */
type Props = {
  answersData: SurveyAnswersResponse
  sortKey: SortKeyType
  onSortChange: (sortKey: SortKeyType, questionId?: string) => void
  tableHeight: number
  currentQuestionId?: string
}

export const SurveyAnswersTable: React.FC<Props> = ({ answersData, sortKey, onSortChange, tableHeight, currentQuestionId }) => {
  const { t } = useTranslation()
  const pickByLanguage = usePickByLanguage()
  const [hoveredCellId, setHoveredCellId] = useState<string | null>(null)

  // ヘッダーテキストを5文字で改行する関数（最大2行まで、3行目以降は2行目の最後に「…」で省略）
  const formatHeaderText = (text: string): React.ReactNode => {
    if (!text) return text
    const chars = Array.from(text)
    const lines: string[] = []
    for (let i = 0; i < chars.length; i += 5) {
      lines.push(chars.slice(i, i + 5).join(''))
    }

    // 最大2行まで表示
    const displayLines = lines.slice(0, 2)
    const hasMore = lines.length > 2

    return (
      <>
        {displayLines.map((line, index) => (
          <React.Fragment key={index}>
            {index === 1 && hasMore ? `${line}…` : line}
            {index < displayLines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </>
    )
  }

  // 回答値を質問タイプに応じてフォーマット
  const formatAnswerValue = (value: any, question: SurveyQuestion): string => {
    if (value === null || value === undefined) {
      return '-'
    }

    if (question.type === 'score' || question.type === 'nps') {
      return String(value)
    }

    if (question.type === 'text') {
      return String(value)
    }

    if (question.type === 'choice_single') {
      return pickByLanguage(value)
    }

    if (question.type === 'choice_multiple') {
      return value
        .map(v => pickByLanguage(v))
        .filter(Boolean)
        .join(', ')
    }

    return String(value)
  }

  // NPSスコアに応じた色を取得（9以上: プロモーター、7以上: パッシブ、それ以下: デトラクター）
  const getNpsColor = (score: number): string => {
    if (score >= 9) return '#4CAF50' // Green for promoters
    if (score >= 7) return '#FFC107' // Yellow for passives
    return '#F44336' // Red for detractors
  }

  if (answersData.data.answers.length === 0) {
    return (
      <div css={noDataStyle}>
        <h1>{t('No data available')}</h1>
      </div>
    )
  }

  // 質問タイプに応じた列幅を取得
  const getColumnWidth = (question: SurveyQuestion): string => {
    if (question.type === 'text') {
      return '300px' // テキストは普通に
    }
    if (question.type === 'choice_multiple') {
      return '250px' // 複数選択も広めに
    }
    // score/npsは狭めに
    if (question.type === 'score' || question.type === 'nps') {
      return '120px'
    }
    return '200px' // その他は標準幅
  }

  return (
    <div css={tableContainerStyle(tableHeight)}>
      <table css={tableStyle}>
        <colgroup>
          <col style={{ width: '200px' }} /> {/* 回答日 */}
          <col style={{ width: '150px' }} /> {/* 宿泊者 */}
          {answersData.data.questions.map(question => (
            <col key={question.id} style={{ width: getColumnWidth(question) }} />
          ))}
        </colgroup>
        <thead css={theadStyle}>
          <tr>
            <SortableTableHeader
              name="submittedAt"
              onChange={onSortChange}
              value={sortKey}
              css={[tableHeaderThStyle, surveyTableHeaderCellStyle, { paddingLeft: 32, width: '200px' }]}
            >
              {formatHeaderText(t('Answer Date'))}
            </SortableTableHeader>
            <th css={[tableHeaderThStyle, surveyTableHeaderCellStyle, { width: '150px' }]}>{formatHeaderText(t('Guest'))}</th>
            {answersData.data.questions.map(question => {
              // 自由記述（text）以外の質問はソート可能
              const isSortable = question.type !== 'text'
              const questionSortKey = `questionId-${question.id}`
              const isCurrentQuestion = sortKey.key === questionSortKey
              const questionTitle = pickByLanguage(question.title)
              const columnWidth = getColumnWidth(question)

              if (isSortable) {
                return (
                  <SortableTableHeader
                    key={question.id}
                    name={questionSortKey}
                    onChange={newSortKey => {
                      onSortChange(newSortKey, question.id)
                    }}
                    value={isCurrentQuestion ? sortKey : { key: '', order: 'asc' }}
                    css={[tableHeaderThStyle, surveyTableHeaderCellStyle, { width: columnWidth }]}
                  >
                    {formatHeaderText(questionTitle)}
                  </SortableTableHeader>
                )
              }

              return (
                <th key={question.id} css={[tableHeaderThStyle, surveyTableHeaderCellStyle, { width: columnWidth }]}>
                  {formatHeaderText(questionTitle)}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {answersData.data.answers.map((answer, index) => (
            <tr key={index} css={tableRowStyle}>
              <td css={[tableCellStyle, { paddingLeft: 32, whiteSpace: 'nowrap' }]}>{dayjs(answer.submittedAt).format(t('DateFormat'))}</td>
              <td css={[tableCellStyle, { whiteSpace: 'nowrap' }]}>
                {answer.reservation.id && answer.reservationId ? (
                  <a style={{ color: '#f2a40b' }} href={`/reservation/${answer.reservation.id}?reservationId=${answer.reservationId}`}>
                    {answer.guestName || '-'}
                  </a>
                ) : (
                  answer.guestName || '-'
                )}
              </td>
              {answersData.data.questions.map(question => {
                const answerValue = answer.answer[question.id]
                const isNps = question.type === 'nps'
                const displayValue = formatAnswerValue(answerValue, question)
                const isTextType = question.type === 'text'
                const cellId = `${index}-${question.id}`
                const isHovered = hoveredCellId === cellId

                return (
                  <TableCell
                    key={question.id}
                    isTextType={isTextType}
                    displayValue={displayValue}
                    isNps={isNps}
                    answerValue={answerValue}
                    getNpsColor={getNpsColor}
                    cellId={cellId}
                    isHovered={isHovered}
                    onMouseEnter={() => setHoveredCellId(cellId)}
                    onMouseLeave={() => setHoveredCellId(null)}
                    tableCellStyle={tableCellStyle}
                  />
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const tableContainerStyle = (height: number) =>
  css({
    width: '100%',
    overflowX: 'auto',
    overflowY: 'auto',
    height: height,
    position: 'relative',
  })

const tableStyle = css({
  width: '100%',
  borderCollapse: 'collapse',
  tableLayout: 'fixed', // 列幅を固定
})

const theadStyle = css({
  position: 'sticky',
  top: 0,
  zIndex: 10,
})

const tableRowStyle = css({
  borderBottom: '1px solid #F2F2F2',
  '&:hover': {
    background: '#F9F9F9',
  },
})

const tableCellStyle = css({
  padding: '20px 16px',
  fontSize: '14px',
  color: '#272727',
  textAlign: 'left',
  overflow: 'hidden',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
})

const textCellWrapperStyle = css({
  position: 'relative',
  width: '100%',
  padding: '20px 16px',
})

const textCellStyle = css({
  whiteSpace: 'normal',
  lineHeight: '1.5',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 3,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxHeight: '4.5em', // lineHeight 1.5 × 3行 = 4.5em
  cursor: 'default',
})

const tooltipStyle = (position: { top: number; left: number; transformX: number }) =>
  css({
    position: 'fixed',
    top: position.top,
    left: position.left,
    transform: `translate(${position.transformX}%, -50%)`,
    background: '#fff',
    color: '#272727',
    fontSize: '14px',
    lineHeight: '1.5',
    padding: '12px 16px',
    borderRadius: '4px',
    whiteSpace: 'normal',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    border: '1px solid #e0e0e0',
    zIndex: 10000,
    width: '400px',
    maxWidth: '400px',
    maxHeight: '600px',
    overflow: 'auto',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    pointerEvents: 'none',
    boxSizing: 'border-box',
  })

const noDataStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
})

// ヘッダーセル用のスタイル（5文字で改行、2行表示）
const surveyTableHeaderCellStyle = css({
  background: '#F5F5F5',
  height: '30px',
  fontSize: '12px',
  fontWeight: 'bold',
  lineHeight: '18px',
  letterSpacing: '0.6px',
  color: '#676767',
  fontFamily: 'YuGothic, "Yu Gothic", "Hiragino Kaku Gothic ProN", "Hiragino Sans", Meiryo, sans-serif',
  whiteSpace: 'normal',
  textAlign: 'left',
  verticalAlign: 'middle',
  padding: '4px 16px',
  wordBreak: 'break-all',
  overflow: 'hidden',
  position: 'sticky',
  top: 0,
  zIndex: 11,
  opacity: 1,
  boxSizing: 'border-box',
  // ソート矢印の位置調整（項目名の直後に配置）
  '& > img': {
    position: 'relative',
    display: 'inline-block',
    verticalAlign: 'middle',
    marginLeft: '4px',
    marginTop: 0,
    top: 'auto',
    right: 'auto',
    left: 'auto',
    transform: 'none',
  },
})

// テキストセルコンポーネント（省略判定とツールチップ表示を管理）
type TableCellProps = {
  isTextType: boolean
  displayValue: string
  isNps: boolean
  answerValue: any
  getNpsColor: (score: number) => string
  cellId: string
  isHovered: boolean
  onMouseEnter: () => void
  onMouseLeave: () => void
  tableCellStyle: ReturnType<typeof css>
}

const TableCell: React.FC<TableCellProps> = ({
  isTextType,
  displayValue,
  isNps,
  answerValue,
  getNpsColor,
  cellId,
  isHovered,
  onMouseEnter,
  onMouseLeave,
  tableCellStyle,
}) => {
  const textRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const cellRef = useRef<HTMLTableCellElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number; transformX: number } | null>(null)

  useEffect(() => {
    if (isTextType && textRef.current && measureRef.current && displayValue !== '-') {
      // 測定用要素の幅を実際のセルの幅に合わせる
      const wrapper = textRef.current.parentElement
      if (wrapper) {
        const cellWidth = wrapper.offsetWidth - 32 // padding分を引く
        measureRef.current.style.width = `${cellWidth}px`
      }

      // レイアウトを再計算させるために少し待つ
      setTimeout(() => {
        if (measureRef.current && textRef.current) {
          const fullHeight = measureRef.current.scrollHeight
          // 3行の高さを正確に計算（lineHeight 1.5 × fontSize 14 × 3行 + padding）
          const lineHeight = 1.5
          const fontSize = 14
          const padding = 40 // 上下のpadding (20px × 2)
          const maxHeightPx = lineHeight * fontSize * 3 + padding
          // 実際の高さが3行を超えている場合のみ省略されていると判定（少し余裕を持たせる）
          setIsTruncated(fullHeight > maxHeightPx + 1)
        }
      }, 10)
    } else {
      setIsTruncated(false)
    }
  }, [isTextType, displayValue])

  const showTooltip = isTextType && isHovered && displayValue !== '-' && isTruncated

  // ホバー時にツールチップの位置を計算
  useEffect(() => {
    if (showTooltip && cellRef.current) {
      const rect = cellRef.current.getBoundingClientRect()
      const tooltipMaxWidth = 400
      const tooltipHalfWidth = tooltipMaxWidth / 2
      const windowWidth = window.innerWidth
      const margin = 16 // 画面端からのマージン

      // 右端に近い場合は左側に表示、それ以外は中央揃え
      let left = rect.left + rect.width / 2
      let transformX = -50 // デフォルトは中央揃え

      if (rect.right + tooltipHalfWidth > windowWidth - margin) {
        // 右端に近い場合：セルの上に被せるように少し右にずらして表示
        left = rect.left + rect.width * 0.3 // セルの30%右にずらす
        transformX = -100 // 右端に合わせる
      } else if (rect.left - tooltipHalfWidth < margin) {
        // 左端に近い場合：右寄せ
        left = rect.left + margin
        transformX = 0
      }

      setTooltipPosition({
        top: rect.top + rect.height / 2,
        left: left,
        transformX: transformX,
      })
    } else {
      setTooltipPosition(null)
    }
  }, [showTooltip])

  return (
    <>
      <td
        ref={cellRef}
        css={[
          tableCellStyle,
          isNps && answerValue !== null && answerValue !== undefined ? css({ color: getNpsColor(answerValue as number) }) : undefined,
          isTextType ? css({ position: 'relative', padding: 0 }) : undefined,
        ]}
        onMouseEnter={isTextType && displayValue !== '-' && isTruncated ? onMouseEnter : undefined}
        onMouseLeave={isTextType && isTruncated ? onMouseLeave : undefined}
      >
        {isTextType ? (
          <div css={textCellWrapperStyle}>
            {/* 表示用テキスト（3行制限） */}
            <div ref={textRef} css={textCellStyle}>
              {displayValue}
            </div>
            {/* 高さ測定用の非表示テキスト */}
            <div
              ref={measureRef}
              css={css({
                position: 'absolute',
                visibility: 'hidden',
                top: 0,
                left: 0,
                padding: '20px 16px',
                fontSize: '14px',
                lineHeight: '1.5',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              })}
            >
              {displayValue}
            </div>
          </div>
        ) : (
          displayValue
        )}
      </td>
      {showTooltip && tooltipPosition && createPortal(<div css={tooltipStyle(tooltipPosition)}>{displayValue}</div>, document.body)}
    </>
  )
}
